import { useState, useEffect, useMemo, useCallback } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Lock, Check, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FORMULAS = [
  { value: 'gite-salle', label: 'Gite + Salle des fetes' },
  { value: 'gite-seul', label: 'Gite seul (20 pers.)' },
  { value: 'appartement-1', label: "L'Appartement Terrasse" },
  { value: 'appartement-2', label: "L'Appartement Panorama" },
];

const MONTHS_FR = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre',
];
const MONTH_SHORT = ['jan', 'fev', 'mar', 'avr', 'mai', 'juin', 'juil', 'aout', 'sept', 'oct', 'nov', 'dec'];
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}
function formatDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function formatMonthList(months) {
  if (!months || months.length === 0) return '';
  const sorted = [...months].sort((a, b) => a - b);
  return sorted.map(m => MONTH_SHORT[m - 1]).join(', ');
}

export default function AvailabilityCalendar() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const [blockedDates, setBlockedDates] = useState([]);
  const [formula, setFormula] = useState('gite-salle');
  const [seasonConfig, setSeasonConfig] = useState({ haute: [5,6,7,8,9], intermediaire: [4,10], basse: [1,2,3,11,12] });
  const now = new Date();
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [viewYear, setViewYear] = useState(now.getFullYear());

  // Fetch season config from API
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/season-config`)
      .then(r => { if (r.data) setSeasonConfig(r.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/availability`).then(r => {
      setBlockedDates(r.data || []);
    }).catch(() => {});
  }, []);

  // Dynamic season resolution based on admin config
  const getSeason = useCallback((month1Indexed) => {
    for (const [season, months] of Object.entries(seasonConfig)) {
      if (months.includes(month1Indexed)) return season;
    }
    return 'basse';
  }, [seasonConfig]);

  // Dynamic season block check
  const getSeasonBlock = useCallback((f, year, month0, day) => {
    const month1 = month0 + 1;
    const season = getSeason(month1);

    if (f === 'appartement-1' || f === 'appartement-2') {
      if (season !== 'basse') return 'Hors saison';
      const wd = new Date(year, month0, day).getDay();
      if (wd === 0 || wd === 6) return 'Week-end';
      if (month0 === 11 && (day === 24 || day === 31)) return 'Reveillon';
      return null;
    }

    if (f === 'gite-seul') {
      if (month0 === 11 && (day === 24 || day === 31)) return 'Reveillon';
      if (season === 'haute') {
        const wd = new Date(year, month0, day).getDay();
        if (wd === 0 || wd === 6) return 'Week-end (haute saison)';
      }
      return null;
    }

    return null;
  }, [getSeason]);

  // Dynamic season notes with actual month names from config
  const getSeasonNote = useCallback((f, month0) => {
    const month1 = month0 + 1;
    const season = getSeason(month1);
    const basseMois = formatMonthList(seasonConfig.basse);
    const hauteMois = formatMonthList(seasonConfig.haute);

    if (f === 'appartement-1' || f === 'appartement-2') {
      if (season !== 'basse') {
        return `Les appartements sont disponibles uniquement en basse saison (${basseMois}).`;
      }
      return 'Appartements : arrivee le lundi, depart au plus tard le vendredi.';
    }

    if (f === 'gite-seul') {
      if (season === 'haute') {
        return `En haute saison (${hauteMois}), le gite seul est disponible du lundi au vendredi uniquement.`;
      }
      return null;
    }

    return null;
  }, [getSeason, seasonConfig]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const isReserved = (dateStr) => {
    return blockedDates.some(b =>
      b.accommodation_type === formula &&
      b.start_date <= dateStr &&
      b.end_date > dateStr
    );
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const today = formatDate(now.getFullYear(), now.getMonth(), now.getDate());
  const seasonNote = useMemo(() => getSeasonNote(formula, viewMonth), [getSeasonNote, formula, viewMonth]);

  return (
    <section id="disponibilites" data-testid="availability-section" className="py-24 md:py-32 bg-[#F2F0E9]">
      <div ref={ref} className={`max-w-[900px] mx-auto px-6 md:px-12 fade-in-section ${isVisible ? 'is-visible' : ''}`}>
        <div className="text-center mb-12">
          <span className="text-[#8A9A5B] text-sm font-medium tracking-[0.2em] uppercase">Planifiez votre sejour</span>
          <h2 data-testid="availability-title" className="font-['Playfair_Display'] text-3xl md:text-5xl text-[#2C3E30] mt-3 tracking-tight">
            Disponibilites
          </h2>
        </div>

        {/* Formula selector */}
        <div className="flex justify-center mb-8">
          <Select value={formula} onValueChange={setFormula}>
            <SelectTrigger data-testid="formula-select" className="w-[280px] rounded-full border-[#E5E5E0] bg-white">
              <SelectValue placeholder="Formule" />
            </SelectTrigger>
            <SelectContent>
              {FORMULAS.map(f => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Season note */}
        {seasonNote && (
          <div className="mb-4 mx-auto max-w-[640px] px-4 py-2.5 rounded-lg bg-[#C4A962]/10 border border-[#C4A962]/20 text-center" data-testid="season-note">
            <p className="text-sm text-[#7A6B3A]">{seasonNote}</p>
          </div>
        )}

        {/* Calendar */}
        <div className="bg-white rounded-2xl shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] overflow-hidden">
          {/* Month nav */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E5E0]">
            <button onClick={prevMonth} className="p-2 hover:bg-[#F5F5F0] rounded-xl transition-colors" data-testid="cal-prev-month">
              <ChevronLeft className="w-5 h-5 text-[#595959]" strokeWidth={1.5} />
            </button>
            <h3 className="font-['Playfair_Display'] text-lg text-[#2C3E30]" data-testid="cal-month-label">
              {MONTHS_FR[viewMonth]} {viewYear}
            </h3>
            <button onClick={nextMonth} className="p-2 hover:bg-[#F5F5F0] rounded-xl transition-colors" data-testid="cal-next-month">
              <ChevronRight className="w-5 h-5 text-[#595959]" strokeWidth={1.5} />
            </button>
          </div>

          {/* Days header */}
          <div className="grid grid-cols-7 px-4 pt-4 pb-2">
            {DAYS_FR.map(d => (
              <div key={d} className="text-center text-xs font-medium text-[#888] py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 px-4 pb-4 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = formatDate(viewYear, viewMonth, day);
              const reserved = isReserved(dateStr);
              const seasonBlock = getSeasonBlock(formula, viewYear, viewMonth, day);
              const isPast = dateStr < today;
              const blocked = reserved || !!seasonBlock;

              return (
                <div
                  key={day}
                  data-testid={`cal-day-${dateStr}`}
                  title={seasonBlock || (reserved ? 'Reserve' : '')}
                  className={`relative text-center py-2.5 rounded-lg text-sm transition-colors ${
                    reserved
                      ? 'bg-red-50 text-red-400 line-through'
                      : seasonBlock
                        ? 'bg-[#E5E5E0]/60 text-[#aaa]'
                        : isPast
                          ? 'text-[#ccc]'
                          : 'text-[#2C3E30] hover:bg-[#8A9A5B]/10'
                  }`}
                >
                  {day}
                  {reserved && (
                    <Lock className="w-2.5 h-2.5 absolute top-1 right-1 text-red-300" strokeWidth={2} />
                  )}
                  {!reserved && seasonBlock && (
                    <X className="w-2.5 h-2.5 absolute top-1 right-1 text-[#bbb]" strokeWidth={2} />
                  )}
                  {!blocked && !isPast && (
                    <Check className="w-2.5 h-2.5 absolute top-1 right-1 text-[#8A9A5B]/40" strokeWidth={2} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="px-6 py-4 border-t border-[#E5E5E0] flex items-center justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#8A9A5B]/20" />
              <span className="text-xs text-[#595959]">Disponible</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-100" />
              <span className="text-xs text-[#595959]">Reserve</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#E5E5E0]" />
              <span className="text-xs text-[#595959]">Hors saison / Non disponible</span>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-[#888] mt-4">
          Les disponibilites tiennent compte des interdependances entre formules et des regles saisonnieres.
        </p>
      </div>
    </section>
  );
}
